import { ProcessedEvent } from '@aldabil/react-scheduler/types';
import { formatDuration, intervalToDuration, parseISO } from 'date-fns';

import { MAINTENANCE_STATUS } from '@/constants/maintenance-status';
import { IAgendaBlock } from '@/interfaces/maintenance/IAgendaBlock';
import {
  IMaintenance,
  IMaintenanceStatus,
} from '@/interfaces/maintenance/IMaintenance';

export function convertMaintenanceToProcessedEvent(
  maintenance: IMaintenance
): ProcessedEvent {
  const startDate = parseISO(maintenance.expectedStartDate);
  const endDate = parseISO(maintenance.expectedEndDate);

  // The agenda previously showed only the VIN, which staff cannot match to a
  // customer at a glance. Not every maintenance belongs to a subscription, so
  // fall back to the VIN alone.
  const customerName =
    maintenance.subscriptionVehicle?.subscription?.customer?.name;

  return {
    event_id: maintenance.id,
    title: customerName
      ? `${customerName} - ${maintenance.vehicle.vin} - ${maintenance.type.displayName}`
      : `${maintenance.vehicle.vin} - ${maintenance.type.displayName}`,
    start: startDate,
    end: endDate,
    deletable: false,
    color: maintenance.status
      ? MAINTENANCE_STATUS[maintenance.status].color
      : MAINTENANCE_STATUS.PENDING.color,
    editable:
      maintenance.status && maintenance.status === IMaintenanceStatus.PENDING,
    maintenance,
    draggable:
    maintenance.status && maintenance.status === IMaintenanceStatus.PENDING,
  };
}

/** Prefix used to tell agenda-block events apart from maintenance events. */
export const AGENDA_BLOCK_EVENT_PREFIX = 'agenda-block:';

export function convertAgendaBlockToProcessedEvent(
  block: IAgendaBlock
): ProcessedEvent {
  return {
    event_id: `${AGENDA_BLOCK_EVENT_PREFIX}${block.id}`,
    title: block.reason ? `Blocked - ${block.reason}` : 'Blocked',
    start: parseISO(block.startDate),
    end: parseISO(block.endDate),
    color: '#616161',
    editable: false,
    draggable: false,
    deletable: true,
    agendaBlock: block,
  };
}

export function parseDurationInSecondsToText(
  durationInSeconds: number
): string {
  const intervalDuration = intervalToDuration({
    start: 0,
    end: durationInSeconds * 1000,
  });

  const text = formatDuration(intervalDuration, {
    delimiter: ' ',
    format: ['hours', 'minutes'],
  });

  return text;
}

export function parseMaintenanceDatesISOToDuration(
  startDateISO: string,
  endDateISO: string
) {
  return (
    (new Date(endDateISO).getTime() - new Date(startDateISO).getTime()) / 1000
  );
}
